import { useEffect, useMemo, useState } from 'react';
import { GitMerge, Download, Pencil, X, Undo2, RotateCcw } from 'lucide-react';
import { color } from '../tokens';
import { type MindMap as MindMapType, type MindNode, type Theme, type Quote } from '../fixtures';
import { useSynth, useSynthNav, useSynthProjectId } from '../SynthesisContext';
import { useOverlays } from '../useOverlays';
import { asText, wrapLines, cleanLabel } from '../asText';
import { apiClient } from '../../../api';

interface StagedGroup {
  label: string;
  note: string;
  members: string[];
}

const PALETTE = ['#7C3AED', '#F59E0B', '#3B82F6', '#EF4444', '#10B981', '#EC4899', '#0891B2', '#D97706'];

function cloneTree(mm: MindMapType): MindMapType {
  return JSON.parse(JSON.stringify(mm || {}));
}

function tokens(text: string): Set<string> {
  return new Set((String(text || '').toLowerCase().match(/[a-z0-9]+/g) || []).filter((w) => w.length > 2));
}

function ensureRoot(mm: MindMapType, client: string): MindMapType {
  if (mm.root && mm.root.children) {
    mm.root.parent = null;
    return mm;
  }
  const roots = Object.keys(mm).filter((k) => mm[k] && mm[k].parent == null);
  if (roots.length === 1 && roots[0] !== 'root') {
    const only = roots[0];
    mm.root = { ...mm[only], parent: null };
    delete mm[only];
    Object.values(mm).forEach((n) => {
      if (n.parent === only) n.parent = 'root';
      if (n.children) n.children = n.children.map((c) => (c === only ? 'root' : c));
    });
    return mm;
  }
  mm.root = { label: client || 'Clusters', parent: null, children: roots.filter((k) => k !== 'root') };
  roots.forEach((k) => { if (mm[k]) mm[k].parent = 'root'; });
  return mm;
}

function hydrateLeaves(mm: MindMapType, themes: Theme[]): MindMapType {
  const clusterIds = mm.root?.children || [];
  const empty = clusterIds.filter((cid) => mm[cid] && !(mm[cid].children || []).length);
  if (!empty.length || !themes.length) return mm;
  const assigned = new Set<string>();
  empty.forEach((cid) => {
    const ctoks = tokens(mm[cid].label);
    const kids: string[] = [];
    themes.forEach((th) => {
      const lid = `theme-${th.rank}`;
      if (assigned.has(lid)) return;
      const ttoks = new Set([...tokens(th.name), ...tokens(th.summary)]);
      let hit = 0;
      ctoks.forEach((t) => { if (ttoks.has(t)) hit += 1; });
      if (hit > 0) {
        mm[lid] = { label: th.name, parent: cid, mentions: th.mentions, entity: th.name, entity_type: 'pain_point' };
        kids.push(lid);
        assigned.add(lid);
      }
    });
    mm[cid].children = kids;
  });
  const leftover = themes.filter((th) => !assigned.has(`theme-${th.rank}`));
  if (leftover.length) {
    const catchId = empty[0];
    const kids = [...(mm[catchId].children || [])];
    leftover.forEach((th) => {
      const lid = `theme-${th.rank}`;
      mm[lid] = { label: th.name, parent: catchId, mentions: th.mentions, entity: th.name, entity_type: 'pain_point' };
      kids.push(lid);
    });
    mm[catchId].children = kids;
  }
  return mm;
}

function buildJourneyTree(stages: Array<{ name: string; color?: string; themes?: number[] }>, themes: Theme[], client: string): MindMapType {
  const tree: MindMapType = {};
  const rootChildren: string[] = [];
  const assigned = new Set<number>();
  stages.forEach((st, i) => {
    const stageId = `jstage_${i}`;
    const children: string[] = [];
    (st.themes || []).forEach((rank) => {
      const theme = themes.find((t) => t.rank === rank);
      if (!theme) return;
      const leafId = `jleaf_${rank}`;
      tree[leafId] = { label: theme.name, parent: stageId, mentions: theme.mentions, entity: theme.name, entity_type: 'pain_point' };
      children.push(leafId);
      assigned.add(rank);
    });
    tree[stageId] = { label: st.name || `Stage ${i + 1}`, color: st.color || PALETTE[i % PALETTE.length], children, parent: 'root' };
    rootChildren.push(stageId);
  });
  const unassigned = themes.filter((t) => !assigned.has(t.rank));
  if (unassigned.length) {
    const uId = 'jstage_unassigned';
    const uChildren: string[] = [];
    unassigned.forEach((t) => {
      const leafId = `jleaf_${t.rank}`;
      tree[leafId] = { label: t.name, parent: uId, mentions: t.mentions, entity: t.name, entity_type: 'pain_point' };
      uChildren.push(leafId);
    });
    tree[uId] = { label: 'Cross-stage', color: '#64748B', children: uChildren, parent: 'root' };
    rootChildren.push(uId);
  }
  tree.root = { label: client || 'Journey', parent: null, children: rootChildren };
  return tree;
}

function matchTheme(leaf: MindNode, themes: Theme[], leafId?: string): Theme | undefined {
  const leafName = asText(leaf.entity || leaf.label);
  const fromId = leafId && /^theme-(\d+)$/.exec(leafId);
  if (fromId) {
    const rank = Number(fromId[1]);
    const hit = themes.find((t) => t.rank === rank);
    if (hit) return hit;
  }
  return themes.find((t) => {
    const tn = asText(t.name);
    return tn === leafName || tn === asText(leaf.label)
      || (leafName.length > 10 && tn.startsWith(leafName.slice(0, 30)))
      || (asText(leaf.label).length > 10 && tn.startsWith(asText(leaf.label).slice(0, 30)));
  });
}

function typeOf(node: MindNode | undefined): string {
  return node?.entity_type ?? '';
}

function keysOf(id: string, node: MindNode | undefined): string[] {
  return node?.keys && node.keys.length ? node.keys : [id];
}

export default function MindMap() {
  const { mindmap, themes, quotes, journey, engagement } = useSynth();
  const { setActive, setQuoteFilter, setExpandedThemeRank } = useSynthNav();
  const projectId = useSynthProjectId();
  const { overlays, loaded, saveOverlay, removeOverlay } = useOverlays(projectId);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groups, setGroups] = useState<StagedGroup[]>([]);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [rollupCounts, setRollupCounts] = useState<Record<string, number>>({});
  const [mode, setMode] = useState<'topical' | 'journey'>('topical');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const [working, setWorking] = useState<MindMapType | null>(null);
  const [ops, setOps] = useState<Array<{ op: string; [k: string]: unknown }>>([]);
  const [moveTarget, setMoveTarget] = useState('');

  const baseTree = useMemo(() => {
    const t = hydrateLeaves(ensureRoot(cloneTree(mindmap), engagement.client), themes);
    return t;
  }, [mindmap, themes, engagement.client]);

  const journeyTree = useMemo(
    () => (journey.length ? buildJourneyTree(journey, themes, engagement.client) : null),
    [journey, themes, engagement.client],
  );

  const tree = mode === 'journey' && journeyTree ? journeyTree : (working || baseTree);
  const isJourneyMode = mode === 'journey';

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/rollup`);
        const data = res?.data;
        if (!alive || !data || data.available !== true || !Array.isArray(data.entities)) return;
        const map: Record<string, number> = {};
        for (const e of data.entities) {
          if (e && e.entity_id != null && typeof e.distinct_source_count === 'number') {
            map[String(e.entity_id)] = e.distinct_source_count;
          }
        }
        setRollupCounts(map);
      } catch { /* engine not live */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    if (!loaded) return;
    const staged = overlays
      .filter((o) => o.overlay && o.overlay.type === 'merge' && Array.isArray(o.overlay.members))
      .map((o) => ({
        label: String(o.overlay.label || o.entity_id),
        note: String(o.overlay.note || 'consultant override'),
        members: o.overlay.members as string[],
      }));
    setGroups(staged);
    setPending(new Set(staged.flatMap((g) => g.members)));
  }, [loaded, overlays]);

  const clusters = useMemo(() => {
    const ids = tree.root?.children || [];
    return ids.map((id) => ({ id, node: tree[id] })).filter((c) => c.node);
  }, [tree]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      clusters.forEach((c) => { if (next[c.id] == null) next[c.id] = true; });
      return next;
    });
  }, [clusters]);

  const selectedTypes = useMemo(() => {
    const t = new Set<string>();
    selected.forEach((id) => t.add(typeOf(tree[id])));
    return t;
  }, [selected, tree]);

  const crossType = selectedTypes.size > 1;
  const canMerge = selected.size >= 2 && !crossType && !isJourneyMode;

  function mutateTree(fn: (t: MindMapType) => void) {
    setWorking((prev) => {
      const t = cloneTree(prev || baseTree);
      fn(t);
      return t;
    });
  }

  function toggleLeaf(id: string) {
    if (editMode) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
      return;
    }
    setDetailId((cur) => (cur === id ? null : id));
  }

  async function mergeSelected() {
    if (!canMerge || saving) return;
    const members = Array.from(selected);
    const label = asText(tree[members[0]]?.label) + (members.length > 1 ? ` +${members.length - 1}` : '');
    const note = 'consultant override';
    setGroups((g) => [...g, { label, note, members }]);
    setPending((p) => { const next = new Set(p); members.forEach((m) => next.add(m)); return next; });
    setSelected(new Set());
    setOps((o) => [...o, { op: 'merge', members, label }]);
    mutateTree((t) => {
      const target = members[0];
      const merged = t[target];
      if (!merged) return;
      const keys = new Set(merged.keys || [target]);
      members.slice(1).forEach((id) => {
        (t[id]?.keys || [id]).forEach((k) => keys.add(k));
        const p = t[t[id]?.parent || ''];
        if (p?.children) p.children = p.children.filter((c) => c !== id);
        delete t[id];
      });
      merged.keys = [...keys];
      merged.mentions = null;
    });
    setSaving(true);
    await saveOverlay(members[0], { type: 'merge', label, note, members });
    setSaving(false);
  }

  function undoGroup(idx: number) {
    const grp = groups[idx];
    if (grp) {
      setPending((p) => {
        const next = new Set(p);
        grp.members.forEach((m) => next.delete(m));
        return next;
      });
      void removeOverlay(grp.members[0]);
    }
    setGroups((g) => g.filter((_, i) => i !== idx));
  }

  function undoLast() {
    if (!ops.length) return;
    setWorking(null);
    setOps([]);
    setSelected(new Set());
  }

  function resetEdits() {
    setWorking(null);
    setOps([]);
    setSelected(new Set());
    setGroups([]);
    setPending(new Set());
  }

  function moveSelected(clusterId: string) {
    if (!clusterId || !selected.size) return;
    const members = Array.from(selected);
    mutateTree((t) => {
      members.forEach((id) => {
        if (!t[id] || !t[clusterId]) return;
        const p = t[t[id].parent || ''];
        if (p?.children) p.children = p.children.filter((c) => c !== id);
        t[id].parent = clusterId;
        t[clusterId].children = [...(t[clusterId].children || []), id];
      });
    });
    setOps((o) => [...o, { op: 'reassign', members, to: clusterId }]);
    setSelected(new Set());
    setMoveTarget('');
  }

  function exportMergeMap() {
    const payload = {
      groups: groups.map((grp) => ({
        label: grp.label,
        note: grp.note,
        members: grp.members.flatMap((id) => keysOf(id, tree[id])),
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'merges.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  const slot = 80; // vertical room per leaf — fits a two-line wrapped card plus a clear gap between cards
  const gap = 30;
  const leafCount = (c: { id: string; node: MindNode }) => (expanded[c.id] ? Math.max((c.node.children || []).length, 1) : 1);
  let totalH = 0;
  clusters.forEach((c) => { totalH += leafCount(c) * slot + gap; });
  const W = 1100;
  const rootX = 70;
  const clusterX = 360;
  const leafX = 740;
  const positions: Record<string, { x: number; y: number }> = {};
  let y = 40;
  clusters.forEach((c) => {
    const hgt = leafCount(c) * slot;
    positions[c.id] = { x: clusterX, y: y + hgt / 2 };
    y += hgt + gap;
  });
  const actualH = Math.max(y, 420);
  const rootY = actualH / 2;
  const leafPos: Record<string, { x: number; y: number }> = {};
  clusters.forEach((c) => {
    if (!expanded[c.id]) return;
    const kids = c.node.children || [];
    const cy = positions[c.id].y;
    const startY = cy - ((kids.length - 1) * slot) / 2;
    kids.forEach((kid, i) => { leafPos[kid] = { x: leafX, y: startY + i * slot }; });
  });
  const curve = (x1: number, y1: number, x2: number, y2: number) => {
    const mx = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
  };

  const detailLeaf = detailId ? tree[detailId] : null;
  const detailTheme = detailLeaf ? matchTheme(detailLeaf, themes, detailId || undefined) : undefined;
  const detailQuotes: Quote[] = detailTheme ? quotes.filter((q) => q.themeIdx === detailTheme.rank) : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>MIND MAP</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            Entity clusters
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
            Leaf counts come from the entity rollup — never the browser. Click a topic to drill down
            to the themes and quotes behind it.
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {journey.length > 0 && (
            <button
              onClick={() => { setMode((m) => m === 'journey' ? 'topical' : 'journey'); setDetailId(null); }}
              style={{
                fontSize: 13, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                background: isJourneyMode ? color.ink : color.surface, color: isJourneyMode ? '#fff' : color.text,
                border: `1px solid ${isJourneyMode ? 'transparent' : color.hairline}`,
              }}
            >
              {isJourneyMode ? 'Topical view' : 'Journey view'}
            </button>
          )}
          <button onClick={() => setExpanded(Object.fromEntries(clusters.map((c) => [c.id, true])))}
            style={{ fontSize: 13, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', background: color.surface, color: color.text, border: `1px solid ${color.hairline}` }}>
            Expand all
          </button>
          <button onClick={() => setExpanded(Object.fromEntries(clusters.map((c) => [c.id, false])))}
            style={{ fontSize: 13, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', background: color.surface, color: color.text, border: `1px solid ${color.hairline}` }}>
            Collapse all
          </button>
          <button
            onClick={() => { setEditMode((v) => !v); setSelected(new Set()); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
              padding: '8px 12px', borderRadius: 8,
              background: editMode ? color.ink : color.surface, color: editMode ? '#fff' : color.text,
              border: `1px solid ${editMode ? 'transparent' : color.hairline}`,
            }}
          >
            <Pencil size={14} /> {editMode ? 'Done editing' : 'Edit groupings'}
          </button>
        </div>
      </div>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      {editMode && !isJourneyMode && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: color.surfaceAlt, border: `1px solid ${color.hairline}`, borderRadius: 10,
          padding: '10px 14px', marginBottom: 16,
        }}>
          <span style={{ fontSize: 12.5, color: color.textMuted }}>{selected.size} selected</span>
          {crossType && (
            <span style={{ fontSize: 12, color: '#B91C1C' }}>
              Can’t merge across entity types — a pain point and an opportunity are different things.
            </span>
          )}
          <button onClick={mergeSelected} disabled={!canMerge || saving} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
            padding: '7px 12px', borderRadius: 8, border: 'none',
            background: canMerge && !saving ? color.purple : color.hairline,
            color: canMerge && !saving ? '#fff' : color.textFaint,
            cursor: saving ? 'wait' : canMerge ? 'pointer' : 'not-allowed',
          }}>
            <GitMerge size={14} /> {saving ? 'Merging…' : 'Merge selected'}
          </button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: color.text }}>
            Move to cluster
            <select
              value={moveTarget}
              onChange={(e) => { setMoveTarget(e.target.value); if (e.target.value) moveSelected(e.target.value); }}
              disabled={!selected.size}
              style={{ fontSize: 13, padding: '6px 8px', borderRadius: 7, border: `1px solid ${color.hairline}` }}
            >
              <option value="">Choose…</option>
              {clusters.map((c) => <option key={c.id} value={c.id}>{asText(c.node.label)}</option>)}
            </select>
          </label>
          <button onClick={undoLast} disabled={!ops.length} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '7px 12px', borderRadius: 8,
            background: color.surface, color: ops.length ? color.text : color.textFaint, border: `1px solid ${color.hairline}`,
            cursor: ops.length ? 'pointer' : 'not-allowed',
          }}>
            <Undo2 size={14} /> Undo
          </button>
          <button onClick={resetEdits} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '7px 12px', borderRadius: 8,
            background: color.surface, color: color.text, border: `1px solid ${color.hairline}`, cursor: 'pointer',
          }}>
            <RotateCcw size={14} /> Reset
          </button>
          <button onClick={exportMergeMap} disabled={!groups.length} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '7px 12px', borderRadius: 8,
            background: color.surface, color: groups.length ? color.text : color.textFaint, border: `1px solid ${color.hairline}`,
            cursor: groups.length ? 'pointer' : 'not-allowed',
          }}>
            <Download size={14} /> Export merge map
          </button>
        </div>
      )}

      {groups.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {groups.map((g, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, background: '#FEF3C7', border: '1px solid #FCD34D',
              borderRadius: 8, padding: '7px 12px', marginBottom: 6, fontSize: 12.5, color: '#B45309',
            }}>
              <GitMerge size={13} />
              <span style={{ flex: 1 }}>
                Merged <strong>{g.members.length}</strong> entities → “{asText(g.label)}” · counts <em>pending re-run</em>
              </span>
              <X size={14} style={{ cursor: 'pointer' }} onClick={() => undoGroup(i)} />
            </div>
          ))}
        </div>
      )}

      {!clusters.length ? (
        <div style={{ padding: 28, border: `1px dashed ${color.separator}`, borderRadius: 12, color: color.textMuted, fontSize: 13 }}>
          No mind map yet. Clusters are built during synthesis from canonicalized entities.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <svg viewBox={`0 0 ${W} ${actualH}`} width="100%" style={{ minWidth: 720, maxWidth: W, display: 'block' }}>
            <defs>
              <pattern id="mmgrid" width="18" height="18" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="#E2E8F0" />
              </pattern>
            </defs>
            <rect width={W} height={actualH} fill="url(#mmgrid)" />
            {clusters.map((c) => {
              const p = positions[c.id];
              const col = c.node.color || PALETTE[0];
              return <path key={`e-${c.id}`} d={curve(rootX + 90, rootY, p.x - 100, p.y)} stroke={col} strokeWidth={2.5} fill="none" opacity={0.85} />;
            })}
            {clusters.filter((c) => expanded[c.id]).flatMap((c) => {
              const p = positions[c.id];
              const col = c.node.color || PALETTE[0];
              return (c.node.children || []).map((kid) => {
                const lp = leafPos[kid];
                if (!lp) return null;
                return <path key={`el-${kid}`} d={curve(p.x + 100, p.y, lp.x - 90, lp.y)} stroke={col} strokeWidth={1.5} fill="none" opacity={0.5} />;
              });
            })}
            {(() => {
              // Root pill: wrap the label instead of cutting it at 26 chars. Grows
              // taller (and the pill loses its full round when it needs two lines).
              const rLines = wrapLines(asText(tree.root?.label) || 'Root', 26, 2);
              const rH = 34 + rLines.length * 15;
              const twoLine = rLines.length > 1;
              return (
                <g>
                  <rect x={rootX - 30} y={rootY - rH / 2} width={200} height={rH} rx={twoLine ? 16 : 30} fill="#0F172A" />
                  <text x={rootX + 70} textAnchor="middle" fontSize={12} fontWeight={700} fill="#FFFFFF">
                    {rLines.map((ln, li) => (
                      <tspan key={li} x={rootX + 70} y={rootY - rH / 2 + 20 + li * 15}>{ln}</tspan>
                    ))}
                  </text>
                  <text x={rootX + 70} y={rootY - rH / 2 + 20 + rLines.length * 15} textAnchor="middle" fontSize={11} fill="#94A3B8">
                    {clusters.length} cluster{clusters.length === 1 ? '' : 's'}
                  </text>
                </g>
              );
            })()}
            {clusters.map((c) => {
              const p = positions[c.id];
              const col = c.node.color || PALETTE[0];
              const kids = c.node.children || [];
              const open = !!expanded[c.id];
              const label = asText(c.node.label);
              // Fixed-width cluster card. The label WRAPS (never mid-word) into the
              // space LEFT of the +/- toggle, so a long name can't run under or past
              // the toggle. Card grows taller for a second line. ~30 chars fit one
              // label line here (accent bar + toggle eat into the 300px width).
              const cw = 300;
              const cLeft = p.x - cw / 2;
              const cLines = wrapLines(label, 30, 2);
              const cBoxH = 24 + cLines.length * 16; // padding + wrapped label + "N topics"
              const cTop = p.y - cBoxH / 2;
              return (
                <g key={c.id} style={{ cursor: 'pointer' }} onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}>
                  <rect x={cLeft} y={cTop} width={cw} height={cBoxH} rx={10} fill="#FFFFFF" stroke={col} strokeWidth={2} />
                  <rect x={cLeft} y={cTop} width={6} height={cBoxH} fill={col} />
                  <text x={cLeft + 14} y={cTop + 18} fontSize={13} fontWeight={600} fill="#0F172A">
                    {cLines.map((ln, li) => (
                      <tspan key={li} x={cLeft + 14} dy={li === 0 ? 0 : 16}>{ln}</tspan>
                    ))}
                  </text>
                  <text x={cLeft + 14} y={cTop + 18 + cLines.length * 16} fontSize={10} fill="#94A3B8">{kids.length} topic{kids.length === 1 ? '' : 's'}</text>
                  <circle cx={cLeft + cw - 20} cy={p.y} r={11} fill={col} />
                  <text x={cLeft + cw - 20} y={p.y + 4} textAnchor="middle" fontSize={12} fontWeight={700} fill="#FFFFFF">{open ? '−' : '+'}</text>
                </g>
              );
            })}
            {Object.keys(leafPos).map((kid) => {
              const lp = leafPos[kid];
              const leaf = tree[kid];
              if (!leaf) return null;
              const parent = tree[leaf.parent || ''];
              const col = parent?.color || '#7C3AED';
              const label = asText(leaf.label);
              const sel = selected.has(kid);
              const open = detailId === kid;
              const isPending = pending.has(kid);
              const recomputed = leaf.entity != null ? rollupCounts[String(leaf.entity)] : undefined;
              const sub = recomputed != null
                ? `${recomputed} stakeholders`
                : (isPending || leaf.mentions == null ? 'pending re-run' : `${leaf.mentions} stakeholder${leaf.mentions === 1 ? '' : 's'}`);
              // Fixed-width leaf card; a long label WRAPS to a second line (never
              // mid-word, never off-canvas) and the card grows taller to fit. The
              // text column is (width - left inset 22 - right padding 16); at ~6.6px
              // per 12px char that's ~38 chars per line inside a 300px card.
              const leafW = 300;
              const lines = wrapLines(label, 38, 2);
              const lh = 15;                        // label line height
              const boxH = 22 + lines.length * lh;  // padding + wrapped lines + sub row
              const topY = lp.y - boxH / 2;
              return (
                <g key={kid} style={{ cursor: 'pointer' }} onClick={() => toggleLeaf(kid)}>
                  <rect x={lp.x - 90} y={topY} width={leafW} height={boxH} rx={8}
                    fill={open || sel ? '#EDE9FE' : '#FFFFFF'}
                    stroke={open || sel ? '#7C3AED' : (isPending ? '#F59E0B' : '#E2E8F0')}
                    strokeWidth={open || sel ? 2 : 1} />
                  <circle cx={lp.x - 78} cy={topY + 14} r={4} fill={col} />
                  <text x={lp.x - 68} y={topY + 17} fontSize={12} fontWeight={500} fill="#0F172A">
                    {lines.map((ln, li) => (
                      <tspan key={li} x={lp.x - 68} dy={li === 0 ? 0 : lh}>{ln}</tspan>
                    ))}
                  </text>
                  <text x={lp.x - 68} y={topY + 17 + lines.length * lh} fontSize={10} fill={isPending && recomputed == null ? '#B45309' : '#94A3B8'}>{sub}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {detailLeaf && (
        <div style={{ marginTop: 14, padding: '16px 18px', background: color.surface, border: `1px solid ${color.hairline}`, borderRadius: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: color.ink }}>{asText(detailLeaf.label)}</div>
              <div style={{ fontSize: 11, color: color.textMuted, marginTop: 2 }}>
                {detailLeaf.mentions || 0} stakeholder{(detailLeaf.mentions || 0) === 1 ? '' : 's'}
                {detailQuotes.length ? ` · ${detailQuotes.length} quote${detailQuotes.length === 1 ? '' : 's'}` : ''}
              </div>
            </div>
            <button onClick={() => setDetailId(null)} style={{
              fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
              background: color.surface, border: `1px solid ${color.hairline}`, color: color.text,
            }}>✕ Close</button>
          </div>
          {detailTheme && (
            <div
              onClick={() => { setExpandedThemeRank(detailTheme.rank); setActive('key-themes'); }}
              style={{
                marginTop: 10, padding: '10px 14px', background: color.pillActiveBg, border: `1px solid ${color.chipBorder}`,
                borderRadius: 8, cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: color.purpleDeep }}>
                Theme #{String(detailTheme.rank).padStart(2, '0')} · {cleanLabel(detailTheme.name)}
              </div>
              {detailTheme.summary && (
                <div style={{ marginTop: 6, fontSize: 12.5, color: color.text, lineHeight: 1.5 }}>{asText(detailTheme.summary)}</div>
              )}
            </div>
          )}
          {detailQuotes.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 6 }}>
                RELATED QUOTES ({detailQuotes.length})
              </div>
              {detailQuotes.slice(0, 8).map((q, i) => (
                <div key={i} style={{ padding: '8px 12px', background: color.surfaceAlt, border: `1px solid ${color.hairline}`, borderRadius: 8, marginBottom: 8, fontSize: 12.5, lineHeight: 1.5 }}>
                  <div style={{ color: color.ink, fontStyle: 'italic' }}>"{asText(q.text).slice(0, 200)}{asText(q.text).length > 200 ? '…' : ''}"</div>
                  <div style={{ marginTop: 4, fontSize: 11, color: color.textMuted }}>
                    {[asText(q.who), asText(q.role), asText(q.file)].filter(Boolean).join(' · ')}
                  </div>
                </div>
              ))}
              {detailQuotes.length > 8 && (
                <button
                  onClick={() => { if (detailTheme) setQuoteFilter(detailTheme.rank); setActive('quotes'); }}
                  style={{ fontSize: 12, color: color.purple, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  + {detailQuotes.length - 8} more — see Evidence
                </button>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 12, color: color.textMuted, fontStyle: 'italic' }}>
              No underlying findings or quotes linked to this topic.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
